// โครงสร้างข้อมูลสำหรับระบบตรวจสอบร้านค้า (Store Verification System)

/**
 * โครงสร้างข้อมูล Store Verification (การตรวจสอบร้านค้า)
 */
export const createVerificationModel = (data) => {
  return {
    // ข้อมูลพื้นฐาน
    storeId: data.storeId || '',
    ownerId: data.ownerId || '',
    
    // ข้อมูลส่วนบุคคล (PDPA Compliant)
    personalInfo: {
      fullName: data.personalInfo?.fullName || '',
      idCardLast4: data.personalInfo?.idCardLast4 || '', // 4 หลักสุดท้าย
      birthDate: data.personalInfo?.birthDate || '', // วัน/เดือน (ไม่เก็บปี)
    },
    
    // Selfie Verification
    selfieVerification: {
      status: data.selfieVerification?.status || 'pending', // pending, approved, rejected
      imageUrl: data.selfieVerification?.imageUrl || '', // URL ของรูป (จะลบหลังตรวจสอบ)
      uploadedAt: data.selfieVerification?.uploadedAt || null,
      reviewedAt: data.selfieVerification?.reviewedAt || null,
      reviewedBy: data.selfieVerification?.reviewedBy || null, // adminId
      reviewNotes: data.selfieVerification?.reviewNotes || '',
      autoDeleteAt: data.selfieVerification?.autoDeleteAt || null, // วันที่ลบอัตโนมัติ (30 วัน)
    },
    
    // เอกสารประกอบ (Optional + Bonus Score)
    supportingDocuments: {
      commercialRegistration: {
        uploaded: data.supportingDocuments?.commercialRegistration?.uploaded || false,
        imageUrl: data.supportingDocuments?.commercialRegistration?.imageUrl || '',
        status: data.supportingDocuments?.commercialRegistration?.status || 'pending', // pending, approved, rejected
        score: data.supportingDocuments?.commercialRegistration?.score || 0, // +20 ถ้าผ่าน
        reviewedAt: data.supportingDocuments?.commercialRegistration?.reviewedAt || null,
        reviewedBy: data.supportingDocuments?.commercialRegistration?.reviewedBy || null,
        reviewNotes: data.supportingDocuments?.commercialRegistration?.reviewNotes || '',
      },
      cannabisLicense: {
        uploaded: data.supportingDocuments?.cannabisLicense?.uploaded || false,
        imageUrl: data.supportingDocuments?.cannabisLicense?.imageUrl || '',
        status: data.supportingDocuments?.cannabisLicense?.status || 'pending',
        score: data.supportingDocuments?.cannabisLicense?.score || 0, // +30 ถ้าผ่าน
        reviewedAt: data.supportingDocuments?.cannabisLicense?.reviewedAt || null,
        reviewedBy: data.supportingDocuments?.cannabisLicense?.reviewedBy || null,
        reviewNotes: data.supportingDocuments?.cannabisLicense?.reviewNotes || '',
      },
      addressProof: {
        uploaded: data.supportingDocuments?.addressProof?.uploaded || false,
        imageUrl: data.supportingDocuments?.addressProof?.imageUrl || '',
        status: data.supportingDocuments?.addressProof?.status || 'pending',
        score: data.supportingDocuments?.addressProof?.score || 0, // +10 ถ้าผ่าน
        reviewedAt: data.supportingDocuments?.addressProof?.reviewedAt || null,
        reviewedBy: data.supportingDocuments?.addressProof?.reviewedBy || null,
        reviewNotes: data.supportingDocuments?.addressProof?.reviewNotes || '',
      },
      taxProof: {
        uploaded: data.supportingDocuments?.taxProof?.uploaded || false,
        imageUrl: data.supportingDocuments?.taxProof?.imageUrl || '',
        status: data.supportingDocuments?.taxProof?.status || 'pending',
        score: data.supportingDocuments?.taxProof?.score || 0, // +15 ถ้าผ่าน
        reviewedAt: data.supportingDocuments?.taxProof?.reviewedAt || null,
        reviewedBy: data.supportingDocuments?.taxProof?.reviewedBy || null,
        reviewNotes: data.supportingDocuments?.taxProof?.reviewNotes || '',
      },
    },
    
    // คะแนนรวม
    verificationScore: data.verificationScore || 0, // 0-175
    riskScore: data.riskScore || 100, // 0-100 (100 = ปลอดภัย, 0 = เสี่ยงสูง)
    
    // สถานะการตรวจสอบ
    status: data.status || 'pending', // pending, in_review, approved, rejected, expired
    verificationType: data.verificationType || 'initial', // initial, renewal
    
    // วันที่สำคัญ
    submittedAt: data.submittedAt || null,
    approvedAt: data.approvedAt || null,
    rejectedAt: data.rejectedAt || null,
    expiresAt: data.expiresAt || null, // วันที่หมดอายุ (1 ปี)
    
    // ผู้ตรวจสอบ
    reviewedBy: data.reviewedBy || null, // adminId
    reviewNotes: data.reviewNotes || '',
    
    // ค่าใช้จ่าย
    cost: {
      amount: data.cost?.amount || 0, // จำนวนเหรียญ
      paidAt: data.cost?.paidAt || null,
      paymentMethod: data.cost?.paymentMethod || 'coins', // coins, package
      packageUsed: data.cost?.packageUsed || null, // packageId ถ้าใช้ Package
    },
    
    // Video Call Booking (ถ้ามี)
    videoCallBooking: {
      bookingId: data.videoCallBooking?.bookingId || null,
      scheduledAt: data.videoCallBooking?.scheduledAt || null,
      duration: data.videoCallBooking?.duration || 0, // นาที
      status: data.videoCallBooking?.status || 'pending', // pending, approved, rejected, completed
      cost: data.videoCallBooking?.cost || 0,
    },
    
    // Metadata
    createdAt: data.createdAt || new Date(),
    updatedAt: data.updatedAt || new Date(),
  };
};

/**
 * โครงสร้างข้อมูล Admin Video Call Booking (การจอง Video Call กับแอดมิน)
 */
export const createAdminVideoCallBookingModel = (data) => {
  return {
    // ข้อมูลพื้นฐาน
    storeId: data.storeId || '',
    ownerId: data.ownerId || '',
    adminId: data.adminId || null, // adminId ที่จะรับ call
    
    // วัตถุประสงค์
    purpose: data.purpose || 'verification', // verification, on_site_inspection, inquiry, other
    purposeDescription: data.purposeDescription || '',
    
    // วันเวลา
    scheduledDate: data.scheduledDate || '', // YYYY-MM-DD
    scheduledTime: data.scheduledTime || '', // HH:mm
    duration: data.duration || 30, // นาที (15, 30, 60)
    
    // สถานะ
    status: data.status || 'pending', // pending, approved, rejected, completed, cancelled
    
    // ค่าใช้จ่าย
    cost: {
      amount: data.cost?.amount || 0, // จำนวนเหรียญ
      paidAt: data.cost?.paidAt || null,
      paymentMethod: data.cost?.paymentMethod || 'coins', // coins, package
      packageUsed: data.cost?.packageUsed || null, // packageId ถ้าใช้ Package
    },
    
    // Video Call Info
    videoCallId: data.videoCallId || null, // เชื่อมกับ videoCalls collection
    channelName: data.channelName || null, // Agora channel name
    
    // การอนุมัติ
    approvedAt: data.approvedAt || null,
    approvedBy: data.approvedBy || null, // adminId
    rejectedAt: data.rejectedAt || null,
    rejectedBy: data.rejectedBy || null, // adminId
    rejectionReason: data.rejectionReason || '',
    
    // หลัง Video Call
    completedAt: data.completedAt || null,
    callNotes: data.callNotes || '', // หมายเหตุจากแอดมิน
    verificationUpdated: data.verificationUpdated || false, // อัพเดทสถานะการตรวจสอบหรือไม่
    
    // Metadata
    createdAt: data.createdAt || new Date(),
    updatedAt: data.updatedAt || new Date(),
  };
};

/**
 * คำนวณคะแนนการตรวจสอบ
 */
export const calculateVerificationScore = (verification) => {
  let score = 0;
  
  // Selfie Verification (100 คะแนน)
  if (verification.selfieVerification?.status === 'approved') {
    score += 100;
  }
  
  // เอกสารประกอบ (Bonus Score)
  const docs = verification.supportingDocuments;
  
  if (docs?.commercialRegistration?.status === 'approved') {
    score += docs.commercialRegistration.score || 20;
  }
  
  if (docs?.cannabisLicense?.status === 'approved') {
    score += docs.cannabisLicense.score || 30;
  }
  
  if (docs?.addressProof?.status === 'approved') {
    score += docs.addressProof.score || 10;
  }
  
  if (docs?.taxProof?.status === 'approved') {
    score += docs.taxProof.score || 15;
  }
  
  return Math.min(score, 175); // สูงสุด 175 คะแนน
};

/**
 * คำนวณ Risk Score (0-100, 100 = ปลอดภัย)
 */
export const calculateRiskScore = (verification) => {
  let riskScore = 100; // เริ่มต้นที่ 100 (ปลอดภัย)
  
  // ลดคะแนนถ้า Selfie ไม่ผ่าน
  if (verification.selfieVerification?.status !== 'approved') {
    riskScore -= 50; // ลด 50 ถ้า Selfie ไม่ผ่าน
  }
  
  // ลดคะแนนถ้าไม่มีเอกสารประกอบ
  const docs = verification.supportingDocuments;
  const hasDocuments = 
    docs?.commercialRegistration?.uploaded ||
    docs?.cannabisLicense?.uploaded ||
    docs?.addressProof?.uploaded ||
    docs?.taxProof?.uploaded;
  
  if (!hasDocuments) {
    riskScore -= 20; // ลด 20 ถ้าไม่มีเอกสาร
  }
  
  // เพิ่มคะแนนถ้ามีเอกสารหลายอย่าง
  let approvedDocs = 0;
  if (docs?.commercialRegistration?.status === 'approved') approvedDocs++;
  if (docs?.cannabisLicense?.status === 'approved') approvedDocs++;
  if (docs?.addressProof?.status === 'approved') approvedDocs++;
  if (docs?.taxProof?.status === 'approved') approvedDocs++;
  
  if (approvedDocs >= 3) {
    riskScore += 10; // เพิ่ม 10 ถ้ามีเอกสารผ่าน 3 อย่างขึ้นไป
  }
  
  return Math.max(0, Math.min(100, riskScore)); // 0-100
};

/**
 * ตรวจสอบว่าการตรวจสอบหมดอายุหรือไม่
 */
export const isVerificationExpired = (verification) => {
  if (!verification.expiresAt) return false;
  return new Date(verification.expiresAt) < new Date();
};

/**
 * ตรวจสอบว่าต้องต่ออายุหรือไม่ (30 วันก่อนหมดอายุ)
 */
export const shouldRenewVerification = (verification) => {
  if (!verification.expiresAt) return false;
  const expiresAt = new Date(verification.expiresAt);
  const now = new Date();
  const daysUntilExpiry = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));
  return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
};

// Cache สำหรับ verification cost config
let verificationCostCache = {
  initialCost: 500,
  renewalCost: 300,
  loaded: false
};

/**
 * โหลด verification cost config จาก Firestore
 * @param {boolean} forceReload - ถ้า true จะ reload แม้ว่าจะ load แล้ว
 */
export const loadVerificationCostConfig = async (forceReload = false) => {
  if (verificationCostCache.loaded && !forceReload) {
    return verificationCostCache;
  }
  
  try {
    const { doc, getDoc } = await import('firebase/firestore');
    const { db } = await import('../firebase/firebaseConfig');
    
    const configRef = doc(db, 'config', 'verification');
    const configSnap = await getDoc(configRef);
    
    if (configSnap.exists()) {
      const config = configSnap.data();
      verificationCostCache = {
        initialCost: config.initialCost ?? 500,
        renewalCost: config.renewalCost ?? 300,
        loaded: true
      };
    } else {
      verificationCostCache.loaded = true;
    }
  } catch (error) {
    console.error('Error loading verification cost config:', error);
    verificationCostCache.loaded = true;
  }
  
  return verificationCostCache;
};

/**
 * กำหนดราคาการตรวจสอบ
 * อ่านจาก cache (ต้องเรียก loadVerificationCostConfig ก่อน)
 */
export const getVerificationCost = (verificationType = 'initial') => {
  if (verificationType === 'renewal') {
    return verificationCostCache.renewalCost;
  }
  return verificationCostCache.initialCost;
};

/**
 * กำหนดราคา Video Call กับแอดมิน
 */
export const getVideoCallCost = (duration) => {
  const costs = {
    15: 300,
    30: 500,
    60: 800,
  };
  return costs[duration] || 500;
};






